import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  makeSyncReduxWithLocalStorageObserver,
  restoreFromLocalStorage,
} from 'app/dataManagement/syncReduxStateWithLocalStorage';
import observeStore from 'app/utils/redux/observeStore';
import _ from 'lodash';

export const EXPORT_SETTINGS_LOCAL_STORAGE_KEY = 'redux.exportSettings';

export type ExportOptions = {
  isExportAllAnnotations: boolean;
};

export type ExportSettings = {
  teleradiology: ExportOptions;
  file: ExportOptions;
  pacs: ExportOptions;
};

const defaultState: ExportSettings = {
  teleradiology: { isExportAllAnnotations: false },
  file: { isExportAllAnnotations: true },
  pacs: { isExportAllAnnotations: false },
};

const initialState: () => ExportSettings = () =>
  _.merge(
    {},
    defaultState,
    restoreFromLocalStorage(EXPORT_SETTINGS_LOCAL_STORAGE_KEY, defaultState)
  );

const exportSettingsSlice = createSlice({
  name: 'exportSettings',
  initialState,
  reducers: {
    setFileExportAnnotations(state: ExportSettings, action: PayloadAction<boolean>) {
      state.file.isExportAllAnnotations = action.payload;
    },
    setPacsExportAnnotations(state: ExportSettings, action: PayloadAction<boolean>) {
      state.pacs.isExportAllAnnotations = action.payload;
    },
    setTeleradiologyExportAnnotations(state: ExportSettings, action: PayloadAction<boolean>) {
      state.teleradiology.isExportAllAnnotations = action.payload;
    },
  },
});

export const {
  setFileExportAnnotations,
  setPacsExportAnnotations,
  setTeleradiologyExportAnnotations,
} = exportSettingsSlice.actions;

export type StoreWithExportSettings = {
  exportSettings: ExportSettings;
};

export const selectExportSettings = (state: StoreWithExportSettings) => state.exportSettings;

export const selectFileExportSettings = (state: StoreWithExportSettings) =>
  state.exportSettings.file;

export const selectPacsExportSettings = (state: StoreWithExportSettings) =>
  state.exportSettings.pacs;

export const selectTeleradiologyExportSettings = (state: StoreWithExportSettings) =>
  state.exportSettings.teleradiology;

export const attachExportSettingsWithLocalStorageSynchronizationObserver = (
  store: StoreWithExportSettings
) => {
  observeStore(
    store,
    selectExportSettings,
    makeSyncReduxWithLocalStorageObserver(EXPORT_SETTINGS_LOCAL_STORAGE_KEY)
  );
};

const exportSettingsReducer = exportSettingsSlice.reducer;

export default exportSettingsReducer;
